import { generateNonce } from 'shared/utils/generate-nonce';

const desktopSignInKey = 'desktop-sign-in';

// The hand-off is worthless once the backend would refuse the code it is paired with.
export const DESKTOP_SIGN_IN_TTL_MS = 10 * 60 * 1000;

export type DesktopSignIn = {
  verifier: string;
  challenge: string;
  createdAt: number;
};

const read = (): DesktopSignIn | undefined => {
  const stored = window.localStorage.getItem(desktopSignInKey);

  if (!stored) {
    return undefined;
  }

  try {
    return JSON.parse(stored);
  } catch {
    return undefined;
  }
};

export const getDesktopSignIn = (): DesktopSignIn | undefined => {
  const stored = read();

  if (!stored?.verifier || !stored.challenge) {
    return undefined;
  }

  // Validity derived at read time rather than cleared on a timer.
  return Date.now() - stored.createdAt < DESKTOP_SIGN_IN_TTL_MS
    ? stored
    : undefined;
};

export const clearDesktopSignIn = () =>
  window.localStorage.removeItem(desktopSignInKey);

// Reusing an unexpired hand-off is what lets the user reopen the browser without stranding the first tab's code.
export const ensureDesktopSignIn = async (): Promise<DesktopSignIn> => {
  const existing = getDesktopSignIn();

  if (existing) {
    return existing;
  }

  const generated = await generateNonce();
  const created: DesktopSignIn = {
    verifier: generated.nonce,
    challenge: generated.hashNonce,
    createdAt: Date.now(),
  };

  window.localStorage.setItem(desktopSignInKey, JSON.stringify(created));

  return created;
};
